import type { CustomLightingInfo } from "../types/sdk";

export const DEFAULT_DOT_MATRIX_ROWS = 7;
export const DEFAULT_DOT_MATRIX_COLS = 7;

/** 有效 RGB 低于此阈值视为灭灯，虚拟点阵不铺黑色、保留格子默认背景 */
export const DOT_MATRIX_OFF_RGB_THRESHOLD = 8;

export const emptyDotMatrixCell = (): CustomLightingInfo => ({
  R: 0,
  G: 0,
  B: 0,
  isCustom: false,
});

export function buildDotMatrixCellKey(row: number, col: number): string {
  return `${row}-${col}`;
}

export function resolveDotMatrixEffectiveRgb(
  cell: CustomLightingInfo | undefined,
  brightness = 100
): { R: number; G: number; B: number } {
  if (!cell) {
    return { R: 0, G: 0, B: 0 };
  }
  const factor = Math.max(0, Math.min(100, brightness)) / 100;
  return {
    R: Math.round(Number(cell.R ?? 0) * factor),
    G: Math.round(Number(cell.G ?? 0) * factor),
    B: Math.round(Number(cell.B ?? 0) * factor),
  };
}

/** 点阵格是否在页面上可见发光（灭灯/近黑时不赋色） */
export function isDotMatrixCellVisuallyLit(
  cell: CustomLightingInfo | undefined,
  brightness = 100
): boolean {
  const { R, G, B } = resolveDotMatrixEffectiveRgb(cell, brightness);
  return (
    R > DOT_MATRIX_OFF_RGB_THRESHOLD ||
    G > DOT_MATRIX_OFF_RGB_THRESHOLD ||
    B > DOT_MATRIX_OFF_RGB_THRESHOLD
  );
}

export function resolveDotMatrixCellBackground(
  cell: CustomLightingInfo | undefined,
  brightness = 100
): string | null {
  if (!isDotMatrixCellVisuallyLit(cell, brightness)) {
    return null;
  }
  const { R, G, B } = resolveDotMatrixEffectiveRgb(cell, brightness);
  return `rgb(${R}, ${G}, ${B})`;
}

export function cloneDotMatrixGrid(
  source: CustomLightingInfo[][] | null | undefined,
  rows: number,
  cols: number
): CustomLightingInfo[][] {
  return Array.from({ length: rows }, (_, row) =>
    Array.from({ length: cols }, (_, col) => {
      const cell = source?.[row]?.[col];
      return cell ? { ...cell } : emptyDotMatrixCell();
    })
  );
}

export function dotMatrixHasVisuallyLitCells(
  matrix: CustomLightingInfo[][] | null | undefined,
  brightness = 100
): boolean {
  if (!matrix?.length) return false;
  return matrix.some((row) =>
    row.some((cell) => isDotMatrixCellVisuallyLit(cell, brightness))
  );
}

/** 是否存在用户自定义格（预设灯效轮询回显不算） */
export function dotMatrixHasCustomCells(
  matrix: CustomLightingInfo[][] | null | undefined
): boolean {
  if (!matrix?.length) return false;
  return matrix.some((row) => row.some((cell) => cell?.isCustom === true));
}

/** 比较两帧点阵数据是否一致，用于轮询时避免无意义 setState */
export function areDotMatrixGridsEqual(
  a: CustomLightingInfo[][] | null | undefined,
  b: CustomLightingInfo[][] | null | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let row = 0; row < a.length; row += 1) {
    if (a[row].length !== b[row].length) return false;
    for (let col = 0; col < a[row].length; col += 1) {
      const left = a[row][col];
      const right = b[row][col];
      if (
        left.R !== right.R ||
        left.G !== right.G ||
        left.B !== right.B ||
        left.isCustom !== right.isCustom
      ) {
        return false;
      }
    }
  }
  return true;
}
